import { createSelector } from 'reselect';
import { constVoid, flow, pipe } from 'fp-ts/function';
import { array, nonEmptyArray, record } from 'fp-ts';
import { TextKey } from '@mews-commander/enums';
import { getStringArrayProp } from '@mews/fp';
import { lt, showSuccessAlert } from '@mews/framework';
import { AsyncAction, CommanderThunkAction, ProductOrderItem } from '@mews-commander/types';
import { divideAmount, sumAllAmounts, createFetchAction } from '@mews-commander/utils';
import { getReservationsDefaultProductOrderItems } from '@mews-commander/legacy-entities/src/selectors/orderItems';
import { prefix } from '../module';
import { cancelOrderItems as cancelOrderItemsApi } from '../actions';

export const NAME = 'orderItems';

const ORDER_ITEMS_CANCEL = prefix('ORDER_ITEMS_CANCEL');

export const cancelOrderItems =
    (orderItemIds: string[]): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { container }) => {
        const promise = dispatch(cancelOrderItemsApi(orderItemIds))
            .then(() => {
                const entity = container().appLocalizationComponent.localizeText(
                    orderItemIds.length === 1 ? TextKey.ProductOrder : TextKey.ProductOrders
                );

                showSuccessAlert({
                    description: lt('CancelEntitySuccessMessage', { entity }),
                });
            })
            .catch(constVoid);
        return dispatch(createFetchAction(ORDER_ITEMS_CANCEL, promise));
    };

export const getCommonReservationsDefaultProductOrderItems = createSelector(
    getReservationsDefaultProductOrderItems,
    getStringArrayProp('serviceOrderIds'),
    (orderItems, serviceOrderIds) => {
        const serviceOrderIdsCount = serviceOrderIds.length;
        if (serviceOrderIdsCount <= 1) {
            return orderItems;
        }

        return pipe(
            orderItems,
            nonEmptyArray.groupBy((orderItem) => orderItem.startUtc ?? ''),
            record.filter((orderItemGroup) => orderItemGroup.length === serviceOrderIdsCount),
            Object.values<ProductOrderItem[]>,
            array.flatten
        );
    }
);

export const getCommonReservationsDefaultProductOrderItemsAverageAmount = createSelector(
    getCommonReservationsDefaultProductOrderItems,
    (orderItems) => {
        const orderItemsCount = orderItems.length;

        if (orderItemsCount === 0) return null;
        if (orderItemsCount === 1) {
            return orderItems[0].fullTimeUnitTotalAmount;
        }

        const totalAmountSum = sumAllAmounts(orderItems.map((orderItem) => orderItem.fullTimeUnitTotalAmount));

        return divideAmount(totalAmountSum, orderItemsCount);
    }
);

export const getHasCommonReservationsDefaultProductOrderItems = flow(
    getCommonReservationsDefaultProductOrderItems,
    (orderItems) => orderItems.length > 0
);
